import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { EmployeeActions } from '../actions/employee/employee.actions';

import type { Employee } from '../interfaces/employee/employee.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FilterChipStrip from './components/FilterChipStrip';
import SegmentedControl from './components/SegmentedControl';
import EmployeeCard from './components/EmployeeCard';
import EditPencil from './components/EditPencil';

import EmployeeEditModal from './EmployeeEditModal';
import CreateEmployeeModal from './CreateEmployeeModal';
import {
  initialsFromName,
  deriveTeam,
  deriveOffice,
  formatDate,
  EmployeeTeam
} from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';

// =============================================================================
//  Employees — v3 redesign
//
//  Available:           first_name, last_name, email, role (enum), erole
//                       (free-text job title), mobile_number, start_date,
//                       status, birthday, id_number, contact_number, ...
//  Stubbed:             team (Advisory/Sponsors/Compliance/Operations) — derived
//                       from `role`/`erole` then hash-bucketed,
//                       office (Cape Town/Johannesburg/Sandton) — hash-bucketed,
//                       qualifications — not on schema; left blank in the v3 view
// =============================================================================

const VIEW_OPTIONS = [
  { value: 'cards' as const, icon: 'cards' as const, ariaLabel: 'Cards' },
  { value: 'rows' as const, icon: 'rows' as const, ariaLabel: 'Rows' }
];

// 'Unassigned' matters now that team is only shown when it is actually
// known — without it those people are reachable only under 'All'.
type TeamKey = 'All' | EmployeeTeam | 'Unassigned';

const TEAM_OPTIONS: ReadonlyArray<{ value: TeamKey; label: string }> = [
  { value: 'All', label: 'All' },
  { value: 'Advisory', label: 'Advisory' },
  { value: 'Sponsors', label: 'Sponsors' },
  { value: 'Compliance', label: 'Compliance' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Unassigned', label: 'Unassigned' }
];

// Departed staff are hidden by default but must stay findable.
type StatusKey = 'active' | 'inactive' | 'all';

const STATUS_OPTIONS = [
  { value: 'active' as const, label: 'Active' },
  { value: 'inactive' as const, label: 'Past' },
  { value: 'all' as const, label: 'All' }
];

const fullName = (e: Employee): string =>
  `${(e.first_name || '').trim()} ${(e.last_name || '').trim()}`.trim() ||
  e.email ||
  `Employee #${e.id}`;

const TEAM_TONE: Record<EmployeeTeam, string> = {
  Advisory: 'muted',
  Sponsors: 'info',
  Compliance: 'warn',
  Operations: 'gold'
};

const EmployeesListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // The API restricts creating and editing staff records to HR roles;
  // mirror that here so the controls aren't offered to everyone.
  const { isHrPrivileged } = useAuth();

  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];

  const [view, setView] = useState<'cards' | 'rows'>('cards');
  const [teamFilter, setTeamFilter] = useState<TeamKey>('All');
  const [statusFilter, setStatusFilter] = useState<StatusKey>('active');
  // Seeded from the store so returning to the page doesn't flash a spinner.
  const [loaded, setLoaded] = useState(employees.length > 0);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);

  // DOWNLOAD_EMPLOYEES saga hits /employees.csv, reads Content-Disposition
  // for the filename, and triggers a browser download via injected anchor.
  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    dispatch(EmployeeActions.downloadEmployees(() => setExporting(false)));
  };

  useEffect(() => {
    dispatch(EmployeeActions.loadEmployees(() => setLoaded(true)));
  }, [dispatch]);

  // Active by default, but 'Past' / 'All' make departed colleagues reachable.
  // Previously inactive staff were dropped with no way to see them at all.
  const isActive = (e: Employee) =>
    (!e.status || e.status === 'active') && e.active_status !== 'inactive';

  const active = useMemo(() => {
    if (statusFilter === 'all') return employees;
    if (statusFilter === 'inactive') return employees.filter((e) => !isActive(e));
    return employees.filter(isActive);
  }, [employees, statusFilter]);

  // Pre-derive team + office per employee (stable across renders).
  const enriched = useMemo(
    () =>
      active.map((e) => ({
        emp: e,
        team: deriveTeam(e),
        office: deriveOffice(e)
      })),
    [active]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(({ emp: e, team }) => {
      if (teamFilter === 'Unassigned') {
        if (team) return false;
      } else if (teamFilter !== 'All' && team !== teamFilter) {
        return false;
      }
      if (q) {
        const hay = `${fullName(e)} ${e.email ?? ''} ${e.erole ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, teamFilter, search]);

  const SORT_ACCESSORS = useMemo(
    () => ({
      name: (r: { emp: Employee }) => fullName(r.emp),
      role: (r: { emp: Employee }) => r.emp.erole || r.emp.role,
      team: (r: { team?: string }) => r.team,
      office: (r: { office?: string }) => r.office,
      email: (r: { emp: Employee }) => r.emp.email,
      phone: (r: { emp: Employee }) => r.emp.mobile_number,
      started: (r: { emp: Employee }) =>
        r.emp.start_date ? new Date(r.emp.start_date as string) : null
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS);

  // Office count for the sub-line.
  const officeCount = useMemo(
    () => new Set(enriched.map((e) => e.office).filter(Boolean)).size,
    [enriched]
  );

  return (
    <>
      <Topbar
        crumbs={['Firm', 'HR — Employees']}
        tools={
          // Wires to /employees.csv via the existing DOWNLOAD_EMPLOYEES saga.
          <button
            className="btn ghost"
            type="button"
            onClick={handleExport}
            disabled={exporting}
            title={exporting ? 'Preparing CSV…' : 'Download CSV of all employees'}
          >
            <Icon name="download" size={14} />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Human Resources</div>
            <h1 className="page-title">Employees</h1>
            <div className="page-sub">
              {!loaded && employees.length === 0
                ? 'Loading…'
                : [
                    filtered.length === active.length
                      ? `${active.length} ${active.length === 1 ? 'person' : 'people'}`
                      : `${filtered.length} of ${active.length} people`,
                    // Only mention offices once at least one is recorded.
                    officeCount > 0
                      ? `${officeCount} office${officeCount === 1 ? '' : 's'}`
                      : undefined,
                    statusFilter === 'active' ? undefined : `showing ${statusFilter}`
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search people…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <SegmentedControl<'cards' | 'rows'>
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
            />
            {isHrPrivileged && (
              <button className="btn primary" type="button" onClick={() => setCreating(true)}>
                <Icon name="plus" size={14} />
                Add employee
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--gap)',
            flexWrap: 'wrap'
          }}
        >
          <FilterChipStrip<TeamKey>
            options={TEAM_OPTIONS}
            value={teamFilter}
            onChange={setTeamFilter}
          />
          <FilterChipStrip<StatusKey>
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">
              {!loaded && employees.length === 0
                ? 'Loading employees…'
                : employees.length === 0
                  ? 'No employees yet.'
                  : statusFilter === 'inactive'
                    ? 'No past employees.'
                    : 'No employees match these filters.'}
            </span>
          </div>
        ) : view === 'cards' ? (
          <div className="grid cards-3">
            {filtered.map(({ emp: e, team, office }) => (
              <EmployeeCard
                key={e.id}
                name={fullName(e)}
                role={e.erole || (e.role ? e.role.replace('_', ' ') : 'Employee')}
                team={team}
                office={office}
                qualifications={e.qualifications || undefined}
                email={e.email}
                onClick={() => navigate(`/employees/${e.id}`)}
                onEdit={isHrPrivileged ? () => setEditingId(e.id as number | string) : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={sort} onToggle={toggle} />
                  <SortableTh label="Role" sortKey="role" sort={sort} onToggle={toggle} />
                  <SortableTh label="Team" sortKey="team" sort={sort} onToggle={toggle} />
                  <SortableTh label="Office" sortKey="office" sort={sort} onToggle={toggle} />
                  <SortableTh label="Email" sortKey="email" sort={sort} onToggle={toggle} />
                  <SortableTh label="Phone" sortKey="phone" sort={sort} onToggle={toggle} />
                  <SortableTh label="Started" sortKey="started" sort={sort} onToggle={toggle} />
                  <th className="actions-cell" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sorted.map(({ emp: e, team, office }) => (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/employees/${e.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="strong">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            background: 'var(--ink)',
                            color: 'var(--gold-soft)',
                            display: 'inline-grid',
                            placeItems: 'center',
                            fontFamily: 'var(--font-body)',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.3
                          }}
                        >
                          {initialsFromName(fullName(e))}
                        </span>
                        {fullName(e)}
                      </div>
                    </td>
                    <td>{e.erole || (e.role ? e.role.replace('_', ' ') : '—')}</td>
                    <td>
                      <span className={`chip ${TEAM_TONE[team]}`}>{team}</span>
                    </td>
                    <td>{office}</td>
                    <td className="muted" style={{ fontSize: 12.5 }}>
                      {e.email}
                    </td>
                    <td className="mono">{e.mobile_number || '—'}</td>
                    <td className="mono">{formatDate(e.start_date)}</td>
                    <td className="actions-cell">
                      {isHrPrivileged && (
                        <EditPencil
                          onClick={() => setEditingId(e.id as number | string)}
                          ariaLabel={`Edit ${fullName(e)}`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingId !== null &&
        (() => {
          const row = filtered.find((r) => String(r.emp.id) === String(editingId));
          if (!row) return null;
          return <EmployeeEditModal employee={row.emp} onClose={() => setEditingId(null)} />;
        })()}

      {creating && <CreateEmployeeModal onClose={() => setCreating(false)} />}
    </>
  );
};

export default EmployeesListV3;
